import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useInviteMember, usePlots } from '@/hooks/use-members'
import { cn } from '@/lib/utils'

interface InviteMemberModalProps {
  open: boolean
  onClose: () => void
}

export function InviteMemberModal({ open, onClose }: InviteMemberModalProps) {
  const invite = useInviteMember()
  const { data: plotsData } = usePlots(open)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('+254')
  const [role, setRole] = useState<'member' | 'committee' | 'estate_manager'>('member')
  const [plotIds, setPlotIds] = useState<string[]>([])
  const [sendSms, setSendSms] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [devInviteUrl, setDevInviteUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName('')
    setEmail('')
    setPhone('+254')
    setRole('member')
    setPlotIds([])
    setSendSms(true)
    setError(null)
    setDevInviteUrl(null)
  }, [open])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setDevInviteUrl(null)
    try {
      const res = await invite.mutateAsync({
        name,
        email,
        phone,
        role,
        plotIds: plotIds.length ? plotIds : undefined,
        sendSms,
      })
      if (res.inviteUrl) {
        setDevInviteUrl(res.inviteUrl)
      } else {
        onClose()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation')
    }
  }

  return (
    <Modal open={open} onClose={onClose} labelledBy="invite-member-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              Members
            </p>
            <h2 id="invite-member-title" className="font-serif text-xl text-ink">
              Invite new member
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-cream-2 hover:text-ink"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          <Input
            label="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            hint="Format: +254XXXXXXXXX"
            required
          />
          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink" htmlFor="invite-role">
              Role
            </label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'member' | 'committee' | 'estate_manager')}
              className="flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
            >
              <option value="member">Member</option>
              <option value="committee">Committee</option>
              <option value="estate_manager">Estate manager</option>
            </select>
          </div>
          {(plotsData?.data.length ?? 0) > 0 && (
            <div className="space-y-2">
              <p className="font-sans text-sm font-medium text-ink">Plot assignment (optional)</p>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-border-2 p-2">
                {plotsData!.data.map((plot) => {
                  const checked = plotIds.includes(plot.id)
                  return (
                    <label
                      key={plot.id}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-cream-2',
                        checked && 'bg-green-pale/40',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) setPlotIds([...plotIds, plot.id])
                          else setPlotIds(plotIds.filter((id) => id !== plot.id))
                        }}
                      />
                      <span>
                        {plot.newNumber}{' '}
                        <span className="text-muted-foreground">({plot.oldNumber})</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )}

          <p className="rounded border border-border-2 bg-cream-2/50 px-3 py-2 font-sans text-xs leading-relaxed text-muted-foreground">
            An email will be sent with a secure link to set up their account. The link expires in{' '}
            <strong className="text-ink">48 hours</strong>.
          </p>

          <label className="flex items-start gap-3 font-sans text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={sendSms}
              onChange={(e) => setSendSms(e.target.checked)}
              className="mt-1"
            />
            <span>
              Also send SMS with portal URL and sign-in email
              <span className="mt-1 block text-xs">Invite link is still sent by email only.</span>
            </span>
          </label>

          {error && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          {devInviteUrl && (
            <div className="rounded border border-green/30 bg-green-pale/50 px-3 py-3">
              <p className="font-sans text-xs font-medium text-status-paid-text">
                Invitation saved (dev — email not configured)
              </p>
              <p className="mt-1 break-all font-sans text-xs text-muted-foreground">
                {devInviteUrl}
              </p>
            </div>
          )}
        </ModalBody>

        {devInviteUrl ? (
          <ModalFooter>
            <div className="flex justify-end">
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </div>
          </ModalFooter>
        ) : (
          <ModalFooter>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button type="submit" loading={invite.isPending} className="w-full sm:w-auto">
                Send invitation
              </Button>
            </div>
          </ModalFooter>
        )}
      </form>
    </Modal>
  )
}
